import { ReactNode } from 'react';

import { ShipmentNumberLink } from '@/components/common/shipments-grid/ShipmentNumberLink';
import { Input } from '@/components/ui/input';
import { EXPORT_ENUMS, SEX } from '@/constants';
import { cssCertifiedFormatter } from '@/grid/value-formatters/cssCertifiedFormatter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { SemenDto } from '@/services/semen-service';
import { formatDateString } from '@/utils';

import { SireLink } from './SireLink';

export type DetailFieldData = {
  label: string;
  value?: string | number | null;
  link?: ReactNode;
};

export const DetailField = ({ label, value, link }: DetailFieldData) => (
  <div className="label-input-group">
    <label>{label}</label>
    {link ? (
      <div className="flex h-10 w-full min-w-0 items-center rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900">
        {link}
      </div>
    ) : (
      <Input
        value={value == null ? '' : String(value)}
        readOnly
        className="h-10 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 shadow-none"
      />
    )}
  </div>
);

export const PreviousStyleDetailField = ({
  label,
  value,
  link,
}: DetailFieldData) => (
  <div className="flex justify-between">
    <span className="text-sm font-medium text-gray-700">{label}:</span>
    <span className="text-sm font-semibold text-gray-900">
      {link ?? (value == null ? '' : String(value))}
    </span>
  </div>
);

const formatSex = (
  sex?: number | string | null,
  useConventionalLabel = false,
) => {
  const numericSex = typeof sex === 'string' ? Number(sex) : sex;

  if (numericSex === SEX.Female) return 'Female';
  if (numericSex === SEX.Male) return 'Male';
  if (numericSex === SEX.Unknown) {
    return useConventionalLabel ? 'Conventional' : 'Unknown';
  }
  return '';
};

export const BatchInformation = ({
  semen,
  onSireSaved,
}: {
  semen?: SemenDto;
  onSireSaved?: () => void;
}) => {
  const useSemenDetailStyleChange = useFeatureFlag('Semen_Detail_Style_Change');
  const isNucleusLinkEnabled = useFeatureFlag('Semen_Detail_Hyperlinks');
  const isSemenDataPointsEnabled = useFeatureFlag('Semen_Data_Points');
  const isSemenTypeLabelEnabled = useFeatureFlag('Semen_Type_Field_Label');
  const isAdditionalInfoFieldsEnabled = useFeatureFlag(
    'Semen_Additional_Info_Fields',
  );
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const isSemenCostFieldEnabled = useFeatureFlag('Semen_Cost_Field');

  const formatExportStatus = (exportStatus?: number) => {
    if (exportStatus == null) return '';

    return (
      Object.entries(EXPORT_ENUMS).find(
        ([, value]) => value === exportStatus,
      )?.[0] ?? String(exportStatus)
    );
  };

  const formatSortStatus = (sortStatus?: number) => {
    return sortStatus === 100000000 ? 'Rejected' : '';
  };

  const formatCost = (cost?: number | null) => {
    if (cost == null) return '';
    return cost.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date?: string | null) => {
    if (!date) return '';
    return formatDateString(date, 'MM-dd-yyyy');
  };

  const baseDetailFields: DetailFieldData[] = [
    {
      label: 'Sire',
      value: semen?.sire?.name,
      link:
        isNucleusLinkEnabled && semen?.sire?.id ? (
          <SireLink sire={semen.sire} onSaved={onSireSaved} />
        ) : undefined,
    },
    { label: 'Pedigree Name', value: semen?.sire?.sire },
    ...(isAdditionalInfoFieldsEnabled
      ? []
      : [{ label: 'Birth Date', value: formatDate(semen?.sire?.birth) }]),
    { label: 'Date Received', value: formatDate(semen?.dateReceived) },
    { label: 'Cane', value: semen?.cane },
    { label: 'Ranch', value: semen?.ownerCode },
    { label: 'Col Code', value: semen?.colCode },
    { label: 'NAAB', value: semen?.naab },
    { label: 'Received From', value: semen?.ownerName },
    {
      label: isSemenTypeLabelEnabled ? 'Semen Type' : 'Sex',
      value: formatSex(semen?.sex, isSemenConventionalTypeEnabled),
    },
    { label: 'Sexed', value: semen?.sexed ? 'Yes' : 'No' },
    {
      label: 'CSS Stamped',
      value:
        semen?.cssCertified != null
          ? cssCertifiedFormatter({ value: semen.cssCertified })
          : '',
    },
    { label: 'Semen Comment', value: semen?.comment },
    { label: 'Storage Location', value: semen?.storageLocation?.name },
    { label: 'Quantity', value: semen?.quantity },
    { label: 'Quantity Received', value: semen?.quantityReceived },
    ...(isSemenCostFieldEnabled
      ? [{ label: 'Cost', value: formatCost(semen?.cost) }]
      : []),
    { label: 'Sold To', value: semen?.soldTo },
    { label: 'Secondary Owner', value: semen?.secondaryOwner },
    { label: 'Export Status', value: formatExportStatus(semen?.export) },
    { label: 'Sort Status', value: formatSortStatus(semen?.sortStatus) },
    { label: 'Critical', value: semen?.critical },
    { label: 'Quantity to Ship', value: semen?.quantityToShip },
  ];

  const semenDataPointDetailFields: DetailFieldData[] = [
    { label: 'Units', value: semen?.units },
    {
      label: 'Shipment',
      value: semen?.shipmentNumber,
      link:
        isNucleusLinkEnabled && semen?.shipmentId ? (
          <ShipmentNumberLink
            shipmentId={semen.shipmentId}
            shipmentNumber={semen.shipmentNumber}
          />
        ) : undefined,
    },
    { label: 'Created By', value: semen?.createdBy },
    ...(isAdditionalInfoFieldsEnabled
      ? []
      : [{ label: 'Created On', value: formatDate(semen?.createdOn) }]),
    ...(isAdditionalInfoFieldsEnabled
      ? []
      : [
          {
            label: 'Record Created On',
            value: formatDate(semen?.overriddenCreatedOn),
          },
        ]),
    { label: 'Semen Type', value: semen?.semenType },
    { label: 'Export Country', value: semen?.country },
    {
      label: 'Short Name',
      value: semen?.shortName,
    },
    {
      label: 'Storage Start Date',
      value: formatDate(semen?.storageStartDate),
    },
    ...(isAdditionalInfoFieldsEnabled
      ? []
      : [
          { label: 'TogId', value: semen?.togId },
          { label: 'Modified By', value: semen?.modifiedBy },
          { label: 'Modified On', value: formatDate(semen?.modifiedOn) },
        ]),
  ];

  const filteredBaseFields = isSemenTypeLabelEnabled
    ? baseDetailFields.filter((f) => f.label !== 'Sexed')
    : baseDetailFields;

  const filteredSemenDataPointDetailFields = isSemenTypeLabelEnabled
    ? semenDataPointDetailFields.filter((f) => f.label !== 'Semen Type')
    : semenDataPointDetailFields;

  const detailFields = isSemenDataPointsEnabled
    ? [...filteredBaseFields, ...filteredSemenDataPointDetailFields]
    : filteredBaseFields;

  if (!useSemenDetailStyleChange) {
    return (
      <div className="grid grid-cols-1 gap-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm lg:grid-cols-2">
        {detailFields.map((field) => (
          <PreviousStyleDetailField key={field.label} {...field} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm md:grid-cols-2 xl:grid-cols-3">
      {detailFields.map((field) => (
        <DetailField key={field.label} {...field} />
      ))}
    </div>
  );
};
